'use client';
import { RESOLUTION_MAP, UPLOAD_SCENE, type UploadScene } from '@/app/constants';
import type { ImagePrompt, ImageRange, SelectedImagePrompt, ServiceError } from '@/app/types';
import { createImage } from '../image-utils';
import { uploadFile } from './upload-file';

const MAX_IMAGE_AREA_4K = RESOLUTION_MAP['4k']; // 4k 面积，作为兜底策略

/**
 * 根据图片的宽高和最大图片面积，计算出目标宽度和高度
 * （此计算方式和算法侧保持一致，但前端不处理 16 的倍数，算法侧会处理，确保保持原始图片的宽高比）
 * @param width 图片宽度
 * @param height 图片高度
 * @param maxImageSize 最大图片面积
 */
export const getScaledImageSize = (
  width: number,
  height: number,
  maxImageSize: number,
): { targetWidth: number; targetHeight: number; scale: number } => {
  const scale = Math.sqrt((height * width) / maxImageSize);

  if (scale >= 0.95 && scale <= 1.05) {
    return { targetWidth: width, targetHeight: height, scale: 1 };
  }
  const targetWidth = Math.floor(width / scale);
  const targetHeight = Math.floor(height / scale);

  return { targetWidth, targetHeight, scale };
};

export const cropImage = async (url: string, range: ImageRange): Promise<string | null> => {
  const image = await createImage(url);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!image || !ctx || !canvas) {
    return null;
  }
  const [x, y, x2, y2] = range;
  const cropWidth = x2 - x;
  const cropHeight = y2 - y;
  canvas.width = cropWidth;
  canvas.height = cropHeight;
  ctx.drawImage(image, x, y, cropWidth, cropHeight, 0, 0, cropWidth, cropHeight);
  return canvas.toDataURL('image/webp');
};

export const resizeImage = async (url: string, maxImageSize?: number) => {
  const image = await createImage(url);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!image || !ctx || !canvas) {
    return null;
  }
  const { width, height } = image;
  if (maxImageSize && width * height > maxImageSize) {
    const { targetWidth, targetHeight, scale } = getScaledImageSize(width, height, maxImageSize);

    canvas.width = targetWidth;
    canvas.height = targetHeight;

    // 计算裁剪区域的中心
    const cropWidth = targetWidth * scale;
    const cropHeight = targetHeight * scale;
    const offsetX = (width - cropWidth) / 2;
    const offsetY = (height - cropHeight) / 2;

    ctx.drawImage(
      image,
      offsetX,
      offsetY,
      cropWidth,
      cropHeight, // 裁剪的源图像区域
      0,
      0,
      targetWidth,
      targetHeight, // 目标画布区域
    );
  } else {
    canvas.width = width;
    canvas.height = height;
    ctx.drawImage(image, 0, 0, width, height);
  }

  return canvas;
};

export const uploadImage = async (
  canvas: HTMLCanvasElement,
  defaultQuality = 0.9,
  scene: UploadScene = UPLOAD_SCENE.VIDU,
  allowTempMode = false,
  userInfo?: unknown,
): Promise<
  | (Omit<Required<SelectedImagePrompt>, 'selectedImageRange'> & {
      selectedId: string;
      error?: ServiceError;
      _tempSelectedImage?: Blob;
    })
  | null
> => {
  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/webp', defaultQuality);
  });

  if (!blob) return null;

  const isTempMode = allowTempMode && !userInfo;

  if (isTempMode) {
    return {
      selectedId: `temp-selected-${performance.now()}`,
      selectedImage: '',
      selectedImagePreview: URL.createObjectURL(blob),
      selectedImageWidth: canvas.width,
      selectedImageHeight: canvas.height,
      _tempSelectedImage: blob,
    };
  }

  const result = await uploadFile({ file: blob, width: canvas.width, height: canvas.height, scene });

  return {
    selectedId: result.id ?? '',
    selectedImage: result.uri,
    selectedImagePreview: URL.createObjectURL(blob),
    selectedImageWidth: canvas.width,
    selectedImageHeight: canvas.height,
    error: result.error,
    _tempSelectedImage: undefined,
  };
};

// 根据分辨率对图片进行 resize 和上传
export const resizeAndUploadImage = async ({
  url,
  maxImageSize,
  defaultQuality = 0.9,
  scene = UPLOAD_SCENE.VIDU,
  allowTempMode = false,
  userInfo,
}: {
  url: string;
  maxImageSize?: number;
  defaultQuality?: number;
  scene?: UploadScene;
  allowTempMode?: boolean;
  userInfo?: unknown;
}): Promise<
  | (Omit<Required<SelectedImagePrompt>, 'selectedImageRange'> & {
      selectedId: string;
      error?: ServiceError;
      _tempSelectedImage?: Blob;
    })
  | null
> => {
  const canvas = await resizeImage(url, maxImageSize);
  if (!canvas) return null;
  return await uploadImage(canvas, defaultQuality, scene, allowTempMode, userInfo);
};

/**
 *
 * @param url 图片URL
 * @param maxImageSize 最大图片尺寸
 * @param defaultQuality 默认质量
 * @returns { blob: Blob; width: number; height: number } | null
 */
export const resizeSourceImage = async ({
  url,
  maxImageSize,
  defaultQuality,
}: {
  url: string;
  maxImageSize?: number;
  defaultQuality?: number;
}): Promise<null | { blob: Blob; width: number; height: number }> => {
  const canvas = await resizeImage(url, maxImageSize);
  if (!canvas) return null;

  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/webp', defaultQuality);
  });

  if (!blob) return null;

  return {
    blob,
    width: canvas.width,
    height: canvas.height,
  };
};

export const handleChangeImageRange = async ({
  range,
  imagePrompt,
  maxImageSize,
  scene = UPLOAD_SCENE.VIDU,
  allowTempMode = false,
  userInfo,
}: {
  range: ImageRange;
  imagePrompt: ImagePrompt;
  maxImageSize: number;
  scene?: UploadScene;
  allowTempMode?: boolean;
  userInfo?: unknown;
}): Promise<{ imagePrompt: ImagePrompt } | { error: ServiceError } | null> => {
  if (
    imagePrompt.selectedImageRange &&
    imagePrompt.selectedImageRange[0] === range[0] &&
    imagePrompt.selectedImageRange[1] === range[1] &&
    imagePrompt.selectedImageRange[2] === range[2] &&
    imagePrompt.selectedImageRange[3] === range[3]
  ) {
    return null;
  }

  const cropImageUrl = await cropImage(imagePrompt.sourceImagePreview, range);
  if (!cropImageUrl) return null;

  const resizeImage = await resizeAndUploadImage({
    url: cropImageUrl,
    maxImageSize,
    defaultQuality: 0.9,
    scene,
    allowTempMode,
    userInfo,
  });
  URL.revokeObjectURL(cropImageUrl);

  if (!resizeImage) return null;

  // 检查上传错误
  if (resizeImage.error) {
    return { error: resizeImage.error };
  }

  // 有新的，释放旧的
  if (imagePrompt.selectedImagePreview) {
    URL.revokeObjectURL(imagePrompt.selectedImagePreview);
  }

  const { error: _, ...imageData } = resizeImage;
  return {
    imagePrompt: { ...imagePrompt, selectedImageRange: range, ...imageData },
  };
};

/**
 * 压缩图片 Blob：
 * 1. 如果限制面积存在且图片面积超过限制面积，先 resize 到限制面积以内
 * 2. 转换为 webp
 * 3. 如果转 webp 后还是超过 maxUploadSize，再 resize 到 4k 面积（兜底策略）
 * @param blob 原始图片 Blob
 * @param maxUploadSize 最大上传大小（字节）
 * @param maxArea 最大图片面积（像素²），不传则不进行限制
 * @returns 压缩后的 Blob 和尺寸信息，如果压缩失败或超过限制返回 null
 */
export const compressImageBlob = async (
  blob: Blob,
  maxUploadSize: number,
  maxArea: number,
): Promise<{ blob: Blob; width: number; height: number } | null> => {
  try {
    // 创建图片对象
    const imageUrl = URL.createObjectURL(blob);
    const image = await createImage(imageUrl);
    URL.revokeObjectURL(imageUrl);

    if (!image) {
      return null;
    }

    const { width: originalWidth, height: originalHeight } = image;

    // 创建 canvas
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      return null;
    }

    const originalArea = originalWidth * originalHeight;

    // 第一步：如果限制面积存在且图片面积超过限制面积，先 resize 到限制面积以内
    let currentWidth = originalWidth;
    let currentHeight = originalHeight;

    if (maxArea && originalArea > maxArea) {
      const scaled = getScaledImageSize(originalWidth, originalHeight, maxArea);
      currentWidth = scaled.targetWidth;
      currentHeight = scaled.targetHeight;
    }

    // 第二步：转换为 webp
    canvas.width = currentWidth;
    canvas.height = currentHeight;
    ctx.drawImage(image, 0, 0, originalWidth, originalHeight, 0, 0, currentWidth, currentHeight);

    let webpBlob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob((b) => resolve(b), 'image/webp', 0.9);
    });

    if (!webpBlob) {
      return null;
    }

    // 如果转 webp 后已经满足大小要求，直接返回
    if (webpBlob.size <= maxUploadSize) {
      return {
        blob: webpBlob,
        width: currentWidth,
        height: currentHeight,
      };
    }

    // 第三步：转 webp 后还是超过 maxUploadSize，再 resize 到 4k 面积（兜底策略）
    const currentArea = currentWidth * currentHeight;
    // 如果当前面积已经小于 4k，说明即使 resize 到 4k 也可能无法满足大小限制
    if (currentArea <= MAX_IMAGE_AREA_4K && webpBlob.type === 'image/webp') {
      console.error('file size is too large even area within 4k', webpBlob.size);
      return null;
    }

    // resize 到 4k 面积
    const scaled4k = getScaledImageSize(currentWidth, currentHeight, MAX_IMAGE_AREA_4K);
    currentWidth = scaled4k.targetWidth;
    currentHeight = scaled4k.targetHeight;

    // 重新绘制到 canvas
    canvas.width = currentWidth;
    canvas.height = currentHeight;
    ctx.drawImage(image, 0, 0, originalWidth, originalHeight, 0, 0, currentWidth, currentHeight);

    // 再次转换为 webp
    webpBlob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob((b) => resolve(b), 'image/webp', 0.9);
    });

    if (!webpBlob) {
      return null;
    }

    // 如果 resize 到 4k 后已经满足大小要求，直接返回
    if (webpBlob.size <= maxUploadSize) {
      return {
        blob: webpBlob,
        width: currentWidth,
        height: currentHeight,
      };
    }

    // 如果 resize 到 4k 后还是超过限制，尝试转换为 jpeg（safari 可能会因为转 webp 失败走到这里，此时图片类型为 png）
    if (webpBlob.type === 'image/png') {
      // 渐进式压缩：从 0.9 开始，每次降低 0.1，直到 0.8
      let quality = 0.9;
      let lastJpegBlob: Blob | null = null;
      while (quality >= 0.8) {
        const jpegBlob = await new Promise<Blob | null>((resolve) => {
          canvas.toBlob((b) => resolve(b), 'image/jpeg', quality);
        });
        if (jpegBlob && jpegBlob.size <= maxUploadSize) {
          return {
            blob: jpegBlob,
            width: currentWidth,
            height: currentHeight,
          };
        }
        lastJpegBlob = jpegBlob;
        quality -= 0.1;
      }
      // 所有质量都尝试完还是超过限制
      console.error(
        'jpegBlob.size > maxUploadSize after all quality attempts',
        lastJpegBlob?.type,
        lastJpegBlob?.size,
        maxUploadSize,
      );
    }

    console.error('webpBlob.size > maxUploadSize', webpBlob.type, webpBlob.size, maxUploadSize);
    return null;
  } catch (error) {
    console.error('Failed to compress image:', error);
    return null;
  }
};
